import React, { useState } from 'react';
import { generateCalendarGrid, formatMonthYear, getPreviousMonth, getNextMonth } from '../../utils/calendar';
import { isKoreanHoliday, getKoreanHolidayName, getMonthOffDaysCount } from '../../utils/holidays';
import { ScheduleStatus } from '../../types/schedule';
import { format, isSameDay } from 'date-fns';

interface CalendarProps {
  onDateClick?: (date: Date) => void;
  scheduleData?: Map<string, ScheduleStatus>;
  disabled?: boolean; // 비활성 계정용
}

const Calendar: React.FC<CalendarProps> = ({ onDateClick, scheduleData = new Map(), disabled = false }) => {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);

  const handlePreviousMonth = () => {
    const { year, month } = getPreviousMonth(currentYear, currentMonth);
    setCurrentYear(year);
    setCurrentMonth(month);
  };

  const handleNextMonth = () => {
    const { year, month } = getNextMonth(currentYear, currentMonth);
    setCurrentYear(year);
    setCurrentMonth(month);
  };

  const calendarDays = generateCalendarGrid(currentYear, currentMonth);
  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
  
  // 이번 달 휴무 가능 일수 계산
  const offDaysCount = getMonthOffDaysCount(currentYear, currentMonth);

  const getStatusColor = (status: ScheduleStatus): string => {
    const normalizedStatus = status.toUpperCase();
    
    switch (normalizedStatus) {
      case 'WORK':
        return 'bg-indigo-100 text-indigo-800'; // 근무: 남색 계열
      case 'OFF':
      case 'APPROVED': // 승인된 휴무
        return 'bg-green-500 text-white'; // 휴무: 초록색
      case 'REQUESTED':
      case 'PENDING': // 대기 중
        return 'bg-orange-400 text-white'; // 신청/대기: 오렌지
      case 'REJECTED': // 거절
        return 'bg-indigo-100 text-indigo-800'; // 거절 = 근무와 동일 (옅은 파랑)
      default:
        return 'bg-white';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* 헤더: 월 네비게이션 + 휴무 일수 */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl md:text-2xl font-bold text-primary">
            {formatMonthYear(currentYear, currentMonth)}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={handlePreviousMonth}
              className="w-10 h-10 flex items-center justify-center bg-base-cream hover:bg-accent hover:text-white rounded-full transition-all duration-200 active:scale-95 text-xl"
              title="이전 달"
            >
              ⬅️
            </button>
            <button
              onClick={handleNextMonth}
              className="w-10 h-10 flex items-center justify-center bg-base-cream hover:bg-accent hover:text-white rounded-full transition-all duration-200 active:scale-95 text-xl"
              title="다음 달"
            >
              ➡️
            </button>
          </div>
        </div>
        
        {/* 휴무 가능 일수 표시 */}
        <div className="text-center bg-base-cream rounded-lg py-2 px-4">
          <p className="text-sm text-gray-600">
            이번 달 휴무 가능 일수:{' '}
            <span className="text-2xl font-black text-red-500">{offDaysCount}</span>
            <span className="text-base font-bold text-gray-700">개</span>
          </p>
        </div>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 gap-2 mb-3">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`text-center font-bold text-base md:text-lg py-3 ${
              index === 0 ? 'text-red-500' : index === 6 ? 'text-blue-500' : 'text-gray-700'
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }

          const dateKey = format(date, 'yyyy-MM-dd');
          const status = scheduleData.get(dateKey);
          const isToday = isSameDay(date, today);
          const isPastMonth = date.getMonth() !== currentMonth - 1;
          const dayOfWeek = date.getDay();
          const isHoliday = isKoreanHoliday(date);
          const holidayName = getKoreanHolidayName(date);

          return (
            <button
              key={dateKey}
              onClick={() => !disabled && onDateClick?.(date)}
              disabled={isPastMonth || disabled}
              className={`
                aspect-square rounded-lg border-2 transition-all duration-200
                ${isToday ? 'border-accent border-4' : 'border-gray-200'}
                ${isPastMonth || disabled ? 'opacity-30 cursor-not-allowed' : 'hover:shadow-lg hover:scale-105 active:scale-95'}
                ${status ? getStatusColor(status) : 'bg-base-dawn hover:bg-base-cream'}
                ${!status && (dayOfWeek === 0 || isHoliday) ? 'text-red-500' : ''}
                ${!status && dayOfWeek === 6 ? 'text-blue-500' : ''}
                ${isHoliday && !status ? 'bg-red-50' : ''}
              `}
              title={holidayName || undefined}
            >
              <div className="flex items-center justify-center h-full">
                <span className="text-xl md:text-2xl font-bold">{date.getDate()}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* 범례 - 3가지만 (신청=대기 통합) */}
      <div className="mt-8 grid grid-cols-3 gap-4 max-w-md mx-auto">
        {/* 휴무 (승인) - 초록 */}
        <div className="flex flex-col items-center justify-center space-y-1 py-3 rounded-lg bg-green-500 text-white">
          <span className="text-2xl">🌴</span>
          <span className="text-sm font-bold">휴무</span>
        </div>
        
        {/* 신청 (대기) - 오렌지 */}
        <div className="flex flex-col items-center justify-center space-y-1 py-3 rounded-lg bg-orange-400 text-white">
          <span className="text-2xl">⏳</span>
          <span className="text-sm font-bold">신청</span>
        </div>
        
        {/* 근무 - 남색 */}
        <div className="flex flex-col items-center justify-center space-y-1 py-3 rounded-lg bg-indigo-100 text-indigo-800">
          <span className="text-2xl">💼</span>
          <span className="text-sm font-bold">근무</span>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
